import { FadeScroller } from "@components/atoms/FadeScroller"
import { cx, tv, VariantProps } from "@utils/tw"
import { animate } from "motion/react"
import { ReactNode, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react"

type TabControlItem = {
  label: ReactNode
  value: string
}

export type TabControlProps = {
  items: TabControlItem[]
  value: string
  onValueChange: (value: string) => void
  className?: string
} & {
  theme?: VariantProps<typeof createStyles>["theme"]
}

export function FloatingTabControl(props: TabControlProps) {
  const id = useId()
  const styles = createStyles(props)

  const innerRef = useRef<HTMLDivElement>(null!)
  const indicatorRef = useRef<HTMLDivElement>(null!)
  const activeRef = useRef<HTMLLabelElement | null>(null)
  const [visiblyActiveValue, setVisiblyActiveValue] = useState<string | null>(null)

  const measure = useCallback(() => {
    const innerBounds = innerRef.current?.getBoundingClientRect()
    const activeBounds = activeRef.current?.getBoundingClientRect()
    if (activeBounds && innerBounds) {
      return {
        left: activeBounds.left - innerBounds.left,
        width: activeBounds.width,
      }
    }
  }, [])

  if (env.client) {
    useLayoutEffect(() => {
      const values = measure()
      if (values) {
        setVisiblyActiveValue(props.value)
        animate(
          indicatorRef.current,
          {
            x: values.left,
            width: values.width,
            opacity: 1,
          },
          {
            type: "spring",
            stiffness: 300,
            damping: 30,
          },
        )
      }

      activeRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      })
    }, [props.value])
  }

  useEffect(() => {
    const ro = new ResizeObserver(() => {
      const values = measure()
      if (values) {
        animate(
          indicatorRef.current,
          {
            x: values.left,
            width: values.width,
            opacity: 1,
          },
          {
            duration: 0,
          },
        )
      }
    })
    ro.observe(innerRef.current!)
    return () => {
      ro.disconnect()
    }
  }, [])

  return (
    <div className={cx(styles.base(), props.className)}>
      <div className={styles.container()}>
        <FadeScroller>
          <div className={styles.inner()} ref={innerRef}>
            {props.items.map((item) => (
              <label
                ref={item.value === props.value ? activeRef : null}
                key={item.value}
                className={styles.tab({ active: item.value === visiblyActiveValue })}
                onPointerDown={(e) => {
                  e.preventDefault()
                  props.onValueChange(item.value)
                }}
              >
                <input
                  type="radio"
                  name={id}
                  value={item.value}
                  checked={item.value === props.value}
                  className="appearance-none absolute opacity-0"
                  onChange={(e) => {
                    if (e.currentTarget.checked) props.onValueChange(item.value)
                  }}
                />
                <span className={styles.label()}>{item.label}</span>
              </label>
            ))}
            <div ref={indicatorRef} className={cx(styles.indicator(), "opacity-0")}></div>
          </div>
        </FadeScroller>
      </div>
    </div>
  )
}

const createStyles = tv({
  base: "flex md:hidden fixed bottom-0 left-0 right-0 p-5 justify-center items-center z-10 text-nowrap pointer-events-none",
  slots: {
    container:
      "type-ui-m flex bg-bg-low group z-1 p-[6px] rounded-[12px] max-w-[calc(100vw-50px)] mx-auto shadow-[0_1px_2px_rgba(0,0,0,0.5)] pointer-events-auto",
    inner: "flex gap-2 relative w-max",
    tab: "bg-transparent text-fg h-[2.3em] px-[0.9em] hover:bg-fg/5 relative inline-flex items-center cursor-pointer transition-colors rounded-[8px]",
    indicator: "absolute top-0 bg-fg z-0 h-full will-change-transform rounded-[8px]",
    label: "block relative z-[1]",
  },
  variants: {
    active: {
      true: {
        tab: "text-bg !bg-transparent",
      },
    },
    theme: {
      dark: "theme-dark",
      light: "theme-light",
    },
  },
  defaultVariants: {
    theme: "dark",
  },
})
